import { Component } from '@theme/component';

/**
 * "¿Cabe en tu espacio?" de MAMU.
 *
 * Lee las medidas del mueble ("275x170cm", "180 x 100 x 80 cm", "120cm diámetro"…), las compara con el ancho y
 * el largo del cuarto (en metros) dejando `data-mamu-cabe-paso` cm de paso (60 por defecto) y escribe el resultado
 * ("Sí cabe: te quedan X cm de paso" / "No cabe: faltan X cm") en una región `aria-live`, junto con el plano a escala.
 * Contrato de marcado: ver "Contrato cabe" al final de MAMU-COMPONENTES.md.
 *
 * La raíz puede ser `<mamu-cabe>` (elemento con el Component de Horizon) o cualquier nodo con `[data-mamu-cabe]`
 * (la sección `mamu-cabe` usa un `<div>`): ambos se manejan con el mismo código y delegando los eventos en `document`.
 */

// <pure>
// Funciones puras (sin DOM), probadas con node. Las medidas salen siempre en centímetros.

/**
 * @typedef {object} Dims
 * @property {number} w - Ancho (cm).
 * @property {number} d - Fondo (cm).
 * @property {boolean} round - Redondo (diámetro).
 */

/**
 * Convierte "1,8" o "1.8" en número.
 *
 * @param {string} value
 * @returns {number}
 */
export function toNumber(value) {
  return Number.parseFloat(value.replace(',', '.'));
}

/**
 * Lee el texto de medidas de un mueble y devuelve su huella en el piso (ancho × fondo, en cm).
 * Si hay varios grupos ("290 × 180 cm · Cama 220 × 180 cm") usa el primero; si hay tres cifras
 * ("180 x 100 x 80") la última es el alto y se ignora. Devuelve `null` si no entiende el texto.
 *
 * @param {string} text
 * @returns {Dims | null}
 */
export function parseMedidas(text) {
  if (typeof text !== 'string') return null;

  const source = text
    .toLowerCase()
    .replace(/[×✕✖*]/g, 'x')
    .replace(/\bpor\b/g, 'x')
    .replace(/\s+/g, ' ');

  const num = '\\d+(?:[.,]\\d+)?';
  const unit = '(?:mm|cm|m)';

  /** @param {number} value @param {string | undefined} u @param {string | undefined} fallback */
  const toCm = (value, u, fallback) => {
    const kind = u ?? fallback;
    if (kind === 'm') return value * 100;
    if (kind === 'mm') return value / 10;
    return value;
  };

  // Diámetro: "120cm diámetro", "120 cm de diámetro", "diámetro 120 cm", "Ø120", "⌀ 90 cm".
  const diameter =
    new RegExp(`(${num})\\s*(${unit})?\\s*(?:de\\s+)?di[aá]m(?:etro|\\.)?`).exec(source) ??
    new RegExp(`(?:di[aá]m(?:etro|\\.)?|[ø⌀])\\s*[:=]?\\s*(${num})\\s*(${unit})?`).exec(source);

  // Rectángulo: 2 o 3 cifras unidas por "x", cada una con su unidad opcional.
  const group = new RegExp(`(${num})\\s*(${unit})?(?![a-wyz])(?:\\s*x\\s*(${num})\\s*(${unit})?(?![a-wyz])){1,2}`).exec(source);

  // Gana lo que aparezca primero en el texto.
  if (diameter && (!group || diameter.index <= group.index)) {
    const [, rawValue, rawUnit] = diameter;
    if (!rawValue) return null;

    const value = toNumber(rawValue);
    const cm = toCm(value, rawUnit, value <= 10 ? 'm' : 'cm');
    return cm > 0 ? { w: cm, d: cm, round: true } : null;
  }

  if (!group) return null;

  const parts = [...group[0].matchAll(new RegExp(`(${num})\\s*(${unit})?`, 'g'))].map((match) => ({
    value: toNumber(match[1] ?? ''),
    unit: match[2],
  }));

  if (parts.length < 2) return null;

  // Las cifras sin unidad heredan la del grupo; si ninguna la trae, ≤ 10 se lee como metros.
  const groupUnit = [...parts].reverse().find((part) => part.unit)?.unit;
  const fallback = groupUnit ?? (Math.max(...parts.map((part) => part.value)) <= 10 ? 'm' : 'cm');

  const w = toCm(parts[0]?.value ?? 0, parts[0]?.unit, fallback);
  const d = toCm(parts[1]?.value ?? 0, parts[1]?.unit, fallback);

  return w > 0 && d > 0 ? { w, d, round: false } : null;
}

/**
 * Compara la huella del mueble con el cuarto, girando el mueble si conviene.
 * `slack` = el menor espacio libre que queda en las dos direcciones; hay que dejar al menos `paso` cm.
 * `w` y `d` son la huella ya orientada (ancho y fondo tal como quedan dentro del cuarto).
 *
 * @param {Dims} dims - Huella del mueble (cm).
 * @param {number} ancho - Ancho del cuarto (cm).
 * @param {number} largo - Largo del cuarto (cm).
 * @param {number} [paso] - Paso que se quiere dejar (cm).
 * @returns {{ fits: boolean, slack: number, missing: number, w: number, d: number }}
 */
export function evaluateFit(dims, ancho, largo, paso = 60) {
  const straight = Math.min(ancho - dims.w, largo - dims.d);
  const turned = Math.min(ancho - dims.d, largo - dims.w);
  const useTurned = turned > straight;
  const slack = useTurned ? turned : straight;

  return {
    fits: slack >= paso,
    slack: Math.floor(slack),
    missing: Math.max(0, Math.ceil(paso - slack)),
    w: useTurned ? dims.d : dims.w,
    d: useTurned ? dims.w : dims.d,
  };
}
// </pure>

/** Textos de respaldo en español; la sección puede cambiarlos con `data-mamu-cabe-txt-*` y `data-mamu-cabe-label-*`. */
const DEFAULT_TEXTS = {
  si: 'Te quedan {cm} cm de paso.',
  no: 'Faltan {cm} cm para dejar {paso} cm de paso.',
  vacio: 'Escribe el ancho y el largo de tu cuarto en metros.',
  invalido: 'Escribe las medidas en metros, por ejemplo 3.5.',
  sinMedidas: 'Esta pieza se ofrece en varios tamaños. Pregúntanos cuál te conviene.',
  labelSi: 'Sí cabe',
  labelNo: 'No cabe',
  plano: 'Cuarto de {ancho} × {largo} m',
};

/** Atributo `data-*` (camelCase en `dataset`) de cada texto configurable. */
const TEXT_ATTRIBUTES = {
  si: 'mamuCabeTxtSi',
  no: 'mamuCabeTxtNo',
  vacio: 'mamuCabeTxtVacio',
  invalido: 'mamuCabeTxtInvalido',
  sinMedidas: 'mamuCabeTxtSinMedidas',
  labelSi: 'mamuCabeLabelSi',
  labelNo: 'mamuCabeLabelNo',
  plano: 'mamuCabeTxtPlano',
};

/** Pausa tras dejar de teclear antes de calcular y anunciar el resultado. */
const TYPING_DELAY = 500;

const SVG_NS = 'http://www.w3.org/2000/svg';
const ROOT_SELECTOR = 'mamu-cabe, [data-mamu-cabe]';

/**
 * Calculadora de una raíz (`<mamu-cabe>` o `[data-mamu-cabe]`).
 */
class CabeTool {
  /** @type {HTMLElement} */
  root;

  /** @type {number} */
  #timer = 0;

  /** Título de la pieza pintada en el enlace y el plano (para sustituirlo al cambiar de pieza). */
  #shownTitle = '';

  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.#shownTitle = this.#piece().title;
  }

  /** Cancela el cálculo pendiente. */
  dispose() {
    clearTimeout(this.#timer);
  }

  /** Mientras se teclea se espera una pausa para no anunciar un resultado por cada dígito. */
  schedule() {
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.calculate(), TYPING_DELAY);
  }

  /** Calcula ya (al confirmar un campo, cambiar de pieza o enviar). */
  commit() {
    clearTimeout(this.#timer);
    this.calculate();
  }

  /**
   * @param {string} selector
   * @returns {HTMLElement | null}
   */
  #el(selector) {
    const el = this.root.querySelector(selector);
    return el instanceof HTMLElement ? el : null;
  }

  /** @returns {HTMLInputElement | null} */
  get #ancho() {
    const el = this.root.querySelector('[data-mamu-cabe-ancho]');
    return el instanceof HTMLInputElement ? el : null;
  }

  /** @returns {HTMLInputElement | null} */
  get #largo() {
    const el = this.root.querySelector('[data-mamu-cabe-largo]');
    return el instanceof HTMLInputElement ? el : null;
  }

  /** @returns {HTMLSelectElement | null} */
  get #select() {
    const el = this.root.querySelector('[data-mamu-cabe-pieza]');
    return el instanceof HTMLSelectElement ? el : null;
  }

  /**
   * Pieza activa: la opción elegida del selector `[data-mamu-cabe-pieza]` o, si no hay selector, la raíz.
   *
   * @returns {{ medidas: string, title: string, url: string }}
   */
  #piece() {
    const option = this.#select?.selectedOptions[0];

    return {
      medidas: option?.dataset.mamuCabeMedidas ?? this.root.dataset.mamuCabeMedidas ?? '',
      title: option?.dataset.title ?? this.root.dataset.mamuCabeTitle ?? '',
      url: option?.dataset.url ?? '',
    };
  }

  /**
   * @param {keyof typeof DEFAULT_TEXTS} key
   * @returns {string}
   */
  #text(key) {
    return this.root.dataset[TEXT_ATTRIBUTES[key]] || DEFAULT_TEXTS[key];
  }

  /**
   * Solo escribe si cambió, para no provocar anuncios ni mutaciones inútiles.
   *
   * @param {Element | null} el
   * @param {string} text
   */
  #setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  /**
   * Escribe el resultado en `[data-mamu-cabe-resultado]` (región `aria-live`): insignia, detalle y enlace.
   *
   * @param {'si' | 'no' | 'none'} state
   * @param {string} label - "Sí cabe" / "No cabe" (vacío si no hay insignia).
   * @param {string} detail
   */
  #show(state, label, detail) {
    const result = this.#el('[data-mamu-cabe-resultado]');
    if (!result) return;

    if (result.dataset.cabe !== state) result.dataset.cabe = state;
    this.root.dataset.cabe = state;

    let badge = result.querySelector('.mamu-cabe__badge');

    if (state === 'none') {
      badge?.remove();
    } else {
      if (!(badge instanceof HTMLElement)) {
        badge = document.createElement('p');
        const estado = document.createElement('span');
        estado.dataset.mamuCabeEstado = '';
        badge.append(estado);
        result.prepend(badge);
      }

      badge.className = `mamu-cabe__badge mamu-cabe__badge--${state}`;
      this.#setIcon(badge, state === 'si' ? 'check' : 'close');
      this.#setText(badge.querySelector('[data-mamu-cabe-estado]'), label);
    }

    let detailEl = result.querySelector('[data-mamu-cabe-detalle]');
    if (!detailEl) {
      detailEl = document.createElement('p');
      detailEl.className = 'mamu-cabe__detail';
      detailEl.setAttribute('data-mamu-cabe-detalle', '');
      const anchor = result.querySelector('.mamu-cabe__badge');
      if (anchor) anchor.after(detailEl);
      else result.prepend(detailEl);
    }
    this.#setText(detailEl, detail);
  }

  /**
   * Cambia el icono de la insignia (check / close) con el mismo SVG que pinta `mamu-icon`.
   *
   * @param {HTMLElement} badge
   * @param {'check' | 'close'} name
   */
  #setIcon(badge, name) {
    const current = badge.querySelector('svg');
    if (current?.classList.contains(`mamu-icon--${name}`)) return;

    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', `mamu-icon mamu-icon--${name}`);
    svg.setAttribute('width', '16');
    svg.setAttribute('height', '16');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.5');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');

    const paths = name === 'check' ? ['M20 6 9 17l-5-5'] : ['M18 6 6 18', 'm6 6 12 12'];
    for (const d of paths) {
      const path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('d', d);
      svg.append(path);
    }

    if (current) current.replaceWith(svg);
    else badge.prepend(svg);
  }

  /**
   * Enlace "Ver {pieza}": apunta a la pieza elegida y cambia su nombre dentro del texto.
   *
   * @param {{ title: string, url: string }} piece
   */
  #updateLink(piece) {
    const link = this.#el('[data-mamu-cabe-enlace]');
    if (!(link instanceof HTMLAnchorElement)) return;

    if (piece.url && link.getAttribute('href') !== piece.url) link.href = piece.url;
    if (this.#shownTitle && piece.title && this.#shownTitle !== piece.title) {
      link.textContent = (link.textContent ?? '').replaceAll(this.#shownTitle, piece.title);
    }
  }

  /**
   * Plano a escala (solo escritorio): proporción del cuarto, tamaño y nombre de la pieza.
   *
   * @param {{ ancho: number, largo: number, w: number, d: number, label: string } | null} plan - Medidas en cm.
   * @param {string} caption
   */
  #updatePlan(plan, caption) {
    const figure = this.#el('[data-mamu-cabe-plano]');
    if (!figure) return;

    figure.hidden = !plan;
    if (!plan) return;

    const room = figure.querySelector('.mamu-cabe__room');
    const piece = figure.querySelector('.mamu-cabe__piece');

    if (room instanceof HTMLElement) room.style.setProperty('--cabe-ratio', (plan.ancho / plan.largo).toFixed(3));
    if (piece instanceof HTMLElement) {
      piece.style.inlineSize = `${Math.min(100, (plan.w * 100) / plan.ancho).toFixed(2)}%`;
      piece.style.blockSize = `${Math.min(100, (plan.d * 100) / plan.largo).toFixed(2)}%`;
      this.#setText(piece.querySelector('span'), plan.label);
    }

    this.#setText(figure.querySelector('[data-mamu-cabe-plano-texto]'), caption);
  }

  /** Calcula con los valores actuales y escribe el resultado. */
  calculate() {
    const piece = this.#piece();
    const dims = parseMedidas(piece.medidas);

    this.#updateLink(piece);
    this.#shownTitle = piece.title || this.#shownTitle;

    if (!dims) {
      this.#show('none', '', this.#text('sinMedidas'));
      this.#updatePlan(null, '');
      return;
    }

    const rawAncho = this.#ancho?.value.trim() ?? '';
    const rawLargo = this.#largo?.value.trim() ?? '';

    if (!rawAncho || !rawLargo) {
      this.#show('none', '', this.#text('vacio'));
      this.#updatePlan(null, '');
      return;
    }

    const ancho = toNumber(rawAncho);
    const largo = toNumber(rawLargo);
    const valid = (/** @type {number} */ n) => Number.isFinite(n) && n > 0 && n <= 50;

    if (!valid(ancho) || !valid(largo)) {
      this.#show('none', '', this.#text('invalido'));
      this.#updatePlan(null, '');
      return;
    }

    const paso = Number.parseFloat(this.root.dataset.mamuCabePaso ?? '') || 60;
    const fit = evaluateFit(dims, ancho * 100, largo * 100, paso);
    const state = fit.fits ? 'si' : 'no';

    const detail = this.#text(state)
      .replaceAll('{cm}', String(fit.fits ? fit.slack : fit.missing))
      .replaceAll('{paso}', String(paso));

    this.#show(state, this.#text(fit.fits ? 'labelSi' : 'labelNo'), detail);

    const format = (/** @type {number} */ n) => n.toFixed(1);
    this.#updatePlan(
      {
        ancho: ancho * 100,
        largo: largo * 100,
        w: fit.w,
        d: fit.d,
        label: `${piece.title} ${Math.round(dims.w)} × ${Math.round(dims.d)} cm`.trim(),
      },
      this.#text('plano').replaceAll('{ancho}', format(ancho)).replaceAll('{largo}', format(largo))
    );
  }
}

/** @type {WeakMap<HTMLElement, CabeTool>} */
const tools = new WeakMap();

/**
 * @param {HTMLElement} root
 * @returns {CabeTool}
 */
function toolFor(root) {
  let tool = tools.get(root);
  if (!tool) {
    tool = new CabeTool(root);
    tools.set(root, tool);
  }
  return tool;
}

/**
 * @param {Event} event
 * @returns {CabeTool | null}
 */
function toolFromEvent(event) {
  const root = event.target instanceof Element ? event.target.closest(ROOT_SELECTOR) : null;
  return root instanceof HTMLElement ? toolFor(root) : null;
}

/**
 * Raíz como elemento personalizado, con el Component de Horizon: calcula al conectarse y libera su temporizador.
 *
 * @extends {Component}
 */
class MamuCabe extends Component {
  connectedCallback() {
    super.connectedCallback();
    toolFor(this).calculate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    tools.get(this)?.dispose();
  }
}

if (!customElements.get('mamu-cabe')) {
  customElements.define('mamu-cabe', MamuCabe);
}

/* Raíces `[data-mamu-cabe]` (div): eventos delegados en `document`; el módulo se carga una sola vez por página. */
const listenerController = new AbortController();
const { signal } = listenerController;

document.addEventListener('input', (event) => {
  if (event.target instanceof HTMLInputElement) toolFromEvent(event)?.schedule();
}, { signal });
document.addEventListener('change', (event) => toolFromEvent(event)?.commit(), { signal });
document.addEventListener('submit', (event) => {
  const tool = toolFromEvent(event);
  if (!tool) return;

  event.preventDefault();
  tool.commit();
}, { signal });

/** Alinea el estado inicial (pintado por el servidor) con la regla de paso en cada raíz de la página. */
function initRoots() {
  for (const root of document.querySelectorAll('[data-mamu-cabe]')) {
    if (root instanceof HTMLElement && !(root instanceof MamuCabe)) toolFor(root).calculate();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRoots, { once: true, signal });
} else {
  initRoots();
}

// Secciones que se recargan en el editor de temas: las raíces nuevas se calculan al llegar.
document.addEventListener('shopify:section:load', initRoots, { signal });
